import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { FiCheckCircle, FiInfo } from "react-icons/fi";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { useTheme } from "../../../ThemeContext";

export const defaultCrSplitData = [
  {
    id: 1,
    resource: "Ramkumar M",
    hours: 10.5,
    rate: 300,
    cost: 3150,
    sharePercent: 48.84,
    earning: 8790.69,
  },
  {
    id: 2,
    resource: "Anu Keerthana",
    hours: 3.5,
    rate: 200,
    cost: 700,
    sharePercent: 10.85,
    earning: 1953.49,
  },
  {
    id: 3,
    resource: "Balakrishnan M",
    hours: 2.0,
    rate: 1000,
    cost: 2000,
    sharePercent: 31.01,
    earning: 5581.4,
  },
  {
    id: 4,
    resource: "Priya",
    hours: 1.5,
    rate: 400,
    cost: 600,
    sharePercent: 9.3,
    earning: 1674.42,
  },
];

const getModalDataGridSx = (isDarkMode) => ({
  border: "none !important",
  borderRadius: 0,
  overflow: "hidden",
  "& .MuiDataGrid-main": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
    borderBottom: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
    fontSize: "12.5px",
    fontWeight: 600,
    color: isDarkMode ? "#9ca3af" : "#475569",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
  },
  "& .MuiDataGrid-cell": {
    fontSize: "12.5px",
    borderBottom: isDarkMode ? "1px solid #2d2d2d" : "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    color: isDarkMode ? "#f1f5f9" : "#334155",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
    minHeight: "44px",
  },
});

/**
 * Change Request Allocation Modal (Screen 4 of HTML 2)
 * Allocates revenue for an approved, billed change request independently of the base project.
 */
const ChangeRequestAllocationModal = ({
  show,
  onHide,
  onConfirm,
  crData = defaultCrSplitData,
}) => {
  const { isDarkMode } = useTheme();
  const modalDataGridSx = useMemo(() => getModalDataGridSx(isDarkMode), [isDarkMode]);
  const rows = useMemo(() => {
    return crData.map((r, i) => ({
      id: r.id || r.resource || i,
      ...r,
    }));
  }, [crData]);

  const columns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => (
          <span className={`fw-medium ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "12.5px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "hours",
        headerName: "CR Hours",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            {Number(params.value).toFixed(1)}
          </span>
        ),
      },
      {
        field: "rate",
        headerName: "Cost Rate",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            ₹{Number(params.value).toLocaleString()}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        width: 120,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px", color: isDarkMode ? "#f1f5f9" : "#334155" }}>
            ₹{Number(params.value).toLocaleString()}
          </span>
        ),
      },
      {
        field: "sharePercent",
        headerName: "Share %",
        width: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span className={isDarkMode ? "text-light-50" : "text-muted"} style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px" }}>
            {Number(params.value).toFixed(2)}%
          </span>
        ),
      },
      {
        field: "earning",
        headerName: "Earning",
        width: 140,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              color: isDarkMode ? "#34d399" : "#16a34a",
              fontSize: "12.5px",
            }}
          >
            ₹{Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
    ],
    [isDarkMode]
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered contentClassName={isDarkMode ? "bg-dark text-white border-secondary" : ""}>
      <Modal.Header closeButton className={`border-bottom pb-3 ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`}>
        <div>
          <Modal.Title className={`h6 fw-bold mb-1 ${isDarkMode ? "text-light" : "text-dark"}`}>
            CTX-CR-014 Run · ₹18,000.00
          </Modal.Title>
          <span className={`badge ${isDarkMode ? "bg-primary text-white" : "bg-primary-subtle text-primary border"} me-2`}>Change Request</span>
          <span className={`badge ${isDarkMode ? "bg-success text-white" : "bg-success-subtle text-success border"}`}>Billed 12 Sep 2026</span>
        </div>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* KPI Cards */}
        <div className="row g-2 mb-3">
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-light-50" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                RECEIVED
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>₹18,000</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-light-50" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                CR COST
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>₹6,450</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-light-50" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                CR HOURS
              </div>
              <div className={`fs-5 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>17.5</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-light-50" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                CR MARGIN
              </div>
              <div className="fs-5 fw-bold text-success mt-1">2.79x</div>
            </div>
          </div>
          <div className="col">
            <div className={`card h-100 border rounded shadow-sm p-2 text-center ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
              <div
                className={`small fw-semibold text-uppercase ${isDarkMode ? "text-light-50" : "text-muted"}`}
                style={{ fontSize: "10.5px", letterSpacing: "0.5px" }}
              >
                EST vs ACTUAL
              </div>
              <div className="fs-5 fw-bold text-warning mt-1">+46%</div>
            </div>
          </div>
        </div>

        {/* MUI DataGrid Table */}
        <Box
          className={`rounded overflow-hidden mb-3 border ${isDarkMode ? "border-secondary" : ""}`}
          sx={{ width: "100%", backgroundColor: isDarkMode ? "#1e1e1e" : "#fff" }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            hideFooterPagination
            disableRowSelectionOnClick
            rowHeight={42}
            slots={{
              footer: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e2e8f0",
                    backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
                    minHeight: "44px",
                    px: 0,
                    width: "100%",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 200, pl: "16px" }}>Total</Box>
                  <Box
                    sx={{
                      width: 110,
                      minWidth: 110,
                      maxWidth: 110,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    17.5
                  </Box>
                  <Box sx={{ width: 110, minWidth: 110, maxWidth: 110, flexShrink: 0 }} />
                  <Box
                    sx={{
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ₹6,450
                  </Box>
                  <Box
                    sx={{
                      width: 110,
                      minWidth: 110,
                      maxWidth: 110,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    100%
                  </Box>
                  <Box
                    sx={{
                      width: 140,
                      minWidth: 140,
                      maxWidth: 140,
                      flexShrink: 0,
                      textAlign: "right",
                      pr: "10px",
                      color: isDarkMode ? "#34d399" : "#16a34a",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ₹18,000.00
                  </Box>
                </Box>
              ),
            }}
            sx={modalDataGridSx}
          />
        </Box>

        <div
          className={`d-flex align-items-center gap-2 mb-3 py-2 px-3 rounded ${
            isDarkMode ? "bg-dark text-info border border-info" : "alert alert-info border-0"
          }`}
          style={{ fontSize: "12px" }}
        >
          <FiInfo size={16} />
          <div>
            <strong>Balakrishnan takes 31% of this change:</strong> Cost share is driven by rate as well as hours. At ₹1,000/hr, two PM hours outweigh three and a half hours of a developer.
          </div>
        </div>

        <div
          className={`d-flex align-items-center gap-2 mb-0 py-2 px-3 rounded ${
            isDarkMode ? "bg-dark text-success border border-success" : "alert alert-success border-0"
          }`}
          style={{ fontSize: "12px" }}
        >
          <FiCheckCircle size={16} />
          <div>✓ Allocates to exactly ₹18,000.00. Rounding remainder applied to largest share (Ramkumar M).</div>
        </div>
      </Modal.Body>
      <Modal.Footer className={`border-top pt-3 ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`}>
        <button
          type="button"
          className="re-btn-cancel"
          style={{ height: "32px", padding: "0 16px", fontSize: "13px" }}
          onClick={onHide}
        >
          Cancel
        </button>
        <button
          type="button"
          className="re-btn-manual"
          style={{ height: "32px", padding: "0 16px", fontSize: "13px" }}
          onClick={onConfirm}
        >
          Confirm CR Run
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeRequestAllocationModal;
